import { Box, Card, CardActionArea, Chip, Typography } from '@mui/material';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { StatusBadge } from '@/shared/components/StatusBadge';
import { PLACEHOLDER_IMAGES } from '@/shared/constants/placeholders';
import { MatchingProductItem } from '../types';

interface MatchingProductCardProps {
  readonly product: MatchingProductItem;
}

function extractCity(address: string): string {
  const commaIdx = address.lastIndexOf(',');
  if (commaIdx >= 0) {
    return address
      .slice(commaIdx + 1)
      .trim()
      .replace(/^\d+\s*/, '')
      .trim();
  }
  return address.split(' ').slice(0, 2).join(' ');
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  const day = d.getDate().toString().padStart(2, '0');
  const month = (d.getMonth() + 1).toString().padStart(2, '0');
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
}

export function MatchingProductCard({ product }: MatchingProductCardProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const firstAlert = product.matchedAlerts[0];
  const alertBadgeLabel = firstAlert
    ? `${firstAlert.materialName} ${extractCity(firstAlert.address)}`
    : '';

  const startFormatted = formatDate(product.offerStartDate);
  const availabilityText = product.offerEndDate
    ? t('alerts.matching.dateRange', 'Du {{from}} au {{to}}', {
        from: startFormatted,
        to: formatDate(product.offerEndDate),
      })
    : t('alerts.matching.dateFrom', 'À partir du {{date}}', {
        date: startFormatted,
      });

  return (
    <Card
      sx={{
        mb: 1.5,
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
      }}
    >
      <CardActionArea
        onClick={() => navigate(`/offers/${product.offerId}`, { state: { from: 'alerts' } })}
        data-testid={`matching-product-card-${product.productId}`}
      >
        <Box sx={{ display: 'flex', p: 2, gap: 2, alignItems: 'flex-start' }}>
          {/* Photo thumbnail */}
          <Box
            sx={{
              width: 100,
              height: 100,
              borderRadius: '10px',
              overflow: 'hidden',
              flexShrink: 0,
              bgcolor: 'grey.100',
            }}
          >
            <img
              src={product.photoUrl ?? PLACEHOLDER_IMAGES.product}
              alt={product.productName}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              onError={e => {
                e.currentTarget.src = PLACEHOLDER_IMAGES.product;
              }}
              data-testid={`matching-product-photo-${product.productId}`}
            />
          </Box>

          {/* Right column */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {/* Alert badge */}
            {firstAlert && (
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}
                data-testid={`matching-product-alert-badge-${product.productId}`}
              >
                <NotificationsActiveOutlinedIcon
                  sx={{ fontSize: 15, color: 'secondary.main', flexShrink: 0 }}
                />
                <Typography
                  variant="caption"
                  sx={{ color: 'text.secondary', fontWeight: 600, lineHeight: 1.2 }}
                  noWrap
                >
                  {alertBadgeLabel}
                </Typography>
              </Box>
            )}

            {/* Offer title — main title */}
            <Typography
              variant="body1"
              fontWeight={700}
              sx={{ lineHeight: 1.35, mb: 0.25 }}
              data-testid={`matching-product-title-${product.productId}`}
              noWrap
            >
              {product.offerTitle}
            </Typography>

            {/* Product name — secondary */}
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ lineHeight: 1.3, mb: 0.5 }}
              noWrap
            >
              {product.productName}
            </Typography>

            {/* Location */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
              <LocationOnIcon sx={{ fontSize: 15, color: 'text.secondary', flexShrink: 0 }} />
              <Typography
                variant="body2"
                color="text.secondary"
                noWrap
                data-testid={`matching-product-address-${product.productId}`}
              >
                {product.offerAddress}
              </Typography>
            </Box>

            {/* Quantity */}
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 0.75 }}
              data-testid={`matching-product-quantity-${product.productId}`}
            >
              {`Quantité\u00a0: ${product.quantity} ${product.unit.labelFr}`}
            </Typography>

            {/* Condition + SOLD badges */}
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75, flexWrap: 'wrap' }}
            >
              <StatusBadge status={product.condition} size="small" />
              {product.offerStatus === 'SOLD' && (
                <Chip
                  label="Vendu"
                  size="small"
                  sx={{
                    bgcolor: 'error.main',
                    color: 'error.contrastText',
                    fontWeight: 600,
                    borderRadius: '8px',
                    px: 1,
                  }}
                  data-testid={`matching-product-sold-badge-${product.productId}`}
                />
              )}
            </Box>

            {/* Availability */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CalendarTodayIcon sx={{ fontSize: 13, color: 'text.secondary', flexShrink: 0 }} />
              <Typography
                variant="caption"
                color="text.secondary"
                data-testid={`matching-product-availability-${product.productId}`}
              >
                {availabilityText}
              </Typography>
            </Box>
          </Box>
        </Box>
      </CardActionArea>
    </Card>
  );
}
